import restClient from "api/restClient";
import { useCallback, useEffect, useState } from "react";

/**
 * Shared data hook for the backend `POST /references-to` endpoint
 * (`reference_resolver`), which lists every record that references a given
 * record -- the inbound / reverse of a normal reference lookup.
 *
 * Factored out of the "Used by" reverse-reference tab so it can also back the
 * inline `form-incoming-reference` schema-form element (a custom object's
 * incoming references rendered/edited in place). Lives in the neutral `utils`
 * layer so both a container (ReferencesUsedByList) and a reusable form
 * component (IncomingReference) can consume it without a component->container
 * import.
 */

/** A single referring record in a reference group. */
export interface ReferenceGroupItem {
    id: string;
    name?: string | null;
}

/**
 * One group of referrers, keyed by (referring record type, column/attribute).
 * Shape mirrors the `/references-to` response exactly; derived view-model types
 * (e.g. the flattened ReferenceRow) live with the presentation transform in
 * usedBySections.
 */
export interface ReferenceGroup {
    referencing_record_type: string;
    via_column?: string;
    via_attribute?: string;
    reference_kind?: string;
    count: number;
    truncated: boolean;
    items: ReferenceGroupItem[];
    referencing_object_template_id?: string;
}

/** Raw `POST /references-to` response body. */
export interface ReferencesToResponse {
    record_type: string;
    record_id: string;
    reference_groups: ReferenceGroup[];
    note?: string;
}

export interface UseReferencesToResult {
    /** null until the first fetch resolves; [] when there are no referrers. */
    groups: ReferenceGroup[] | null;
    isLoading: boolean;
    error: string | null;
    /** Re-run the fetch (e.g. after a referring record is added/removed). */
    refetch: () => void;
}

/**
 * Fetch the records that reference `(recordType, id)`.
 *
 * `recordType` is the FE resource name (any case); it is upper-cased for the
 * backend record_type enum, matching the existing used-by tab call.
 */
export const useReferencesTo = (recordType: string, id: string): UseReferencesToResult => {
    const [groups, setGroups] = useState<ReferenceGroup[] | null>(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        // Skip the lookup when there is no record to resolve. A caller may render
        // before its backing record exists (the create screen) or without a
        // record context at all (e.g. a read-only schema-form preview on the
        // custom-object canvas, where useSchemaFormRecord has no recordType). The
        // backend record_type validator rejects an empty record_type with a 400,
        // so firing the call can never succeed — and there are no inbound
        // references to a record that is not yet identified. Report "no
        // references" without a network round-trip.
        if (!recordType || !id) {
            setGroups([]);
            setError(null);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        restClient
            .post("/references-to", {
                record_type: recordType.toUpperCase(),
                id,
            })
            .then((response) => {
                if (!cancelled) {
                    const data = response?.data as ReferencesToResponse | undefined;
                    setGroups(data?.reference_groups ?? []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(
                        err?.response?.data?.message ??
                            err?.message ??
                            "Failed to load references."
                    );
                    setGroups([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [recordType, id, reloadToken]);

    const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

    return { groups, isLoading, error, refetch };
};
